import { Box, Button, Stack } from "@mantine/core"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useHelpContext } from "@dehub/theme"
import { useNavigate, useParams } from "react-router"
import useSWR from "swr"
import { DeliveryApi } from "../../apis"
import { translateBackendError } from "../delivery/translate-error"
import { DeniedCard, ListState } from "../ui"
import { DnDetailPage } from "./DnDetailPage"
import type { DnDetail } from "./dn-detail-utils"

type OrderFields = Pick<DnDetail, "salesCode" | "paidAmount" | "amountToPay">

// GET /ord/delivery_note/:id nests the order's code and payment amounts under
// `order`; /delivery_note/list flattens them. The page reads the flat names.
const withOrderFields = (dn: DnDetail & { order?: OrderFields | null }): DnDetail => ({
  ...dn,
  salesCode: dn.salesCode ?? dn.order?.salesCode ?? null,
  paidAmount: dn.paidAmount ?? dn.order?.paidAmount ?? null,
  amountToPay: dn.amountToPay ?? dn.order?.amountToPay ?? null,
})

/** /supplier-delivery/detail/:id — loading, denied (ORD_DN_DETAIL), error and not-found states around the page. */
export function SupplierDeliveryDistributingDetail() {
  const { t } = useTranslation("delivery")
  const params = useParams()
  const navigate = useNavigate()

  const { data, isLoading, error, mutate } = useSWR<DnDetail>(
    params.id ? `/delivery_note/detail/${params.id}` : null,
    async () => withOrderFields(await DeliveryApi.get(params.id as string)),
  )
  // @dehub/help: the delivery note status drives the drawer's «Одоо» card and status tab.
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => {
    const st = data?.deliveryNoteStatus ?? undefined
    setHelpContext({
      status: st,
      vars: { needsRoute: st === "DRAFT" ? 1 : 0, needsLoading: st === "ASSIGNED" ? 1 : 0, needsHandover: st === "PENDING_HANDOVER" ? 1 : 0, loaded: st === "LOADED" ? 1 : 0, inTransit: st === "DELIVERING" ? 1 : 0, delivered: st === "DELIVERED" ? 1 : 0 },
    })
  }, [data, setHelpContext])

  if (isLoading) {
    return (
      <Box p="md">
        <ListState state="loading" columns={3} />
      </Box>
    )
  }

  if (error) {
    const status = (error as { statusCode?: number })?.statusCode
    return (
      <Box p="md">
        {status === 403 ? (
          <DeniedCard permission="ORD_DN_DETAIL" title={t("dnDetail.deniedTitle")} />
        ) : (
          <ListState state="error" title={t("dnDetail.notFound")} description={translateBackendError(error)} onRetry={() => mutate()} errorCode={status} />
        )}
      </Box>
    )
  }

  if (!data) {
    return (
      <Stack p="md" gap="md" align="flex-start">
        <ListState state="empty" title={t("dnDetail.notFound")} />
        <Button variant="default" onClick={() => navigate("/supplier-delivery")}>
          {t("dnDetail.backToList")}
        </Button>
      </Stack>
    )
  }

  return (
    <Box p="md">
      <DnDetailPage dn={data} onRefresh={() => mutate()} />
    </Box>
  )
}
